/*
Copyright (c) 2012-2025 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
/** @jsx jsx */
import { jsx, css } from "@emotion/core"
import { FC, Fragment, useCallback, useEffect, useState } from "react"
import { Drawer, Button, Input, Select } from "@duxton-web/ui"
import { Form } from "grab-ui"
import { useDispatch, useSelector } from "react-redux"
import { debounce, isEmpty, replace } from "lodash"
import { AnyAction } from "redux"
import {
  AxiosAction,
  ResponseWithError,
  CreateCategoryResponse,
} from "types/api"
import {
  MenuTranslationRequestData,
  SellingTime,
  TranslationDetail,
  TranslationSetting,
} from "types/models/menu"
import { actionCreator } from "store/store"
import { AppState } from "store/store.type"
import { hasFormErrors, getValidationProps } from "utils/form_helper"
import { isMenuSupportedLanguages } from "utils/menu_helper"
import { ConfirmDeleteCategoryOptions } from "utils/category_helper"
import {
  EventConflictStatus,
  getSellingTimeByID,
  getSellingTimeMetadata,
  useConflictConfirmPayload,
} from "utils/selling_time_helper"
import { ServiceHourList } from "components/list/ServiceHourList"
import { useTranslation } from "common/utils/i18n"
import { useTranslationsMapTyped } from "hooks/useTranslationsMapTyped"
import { TroyWebTracker, getResponseErrorEventLabels } from "utils/tracking"
import { getUCMConfig } from "utils/config"
import {
  EventAction,
  EventActionEng,
  EventCategory,
  EventCategoryEng,
  EventEntryPoint,
} from "constants/tracking"
import { SellingTimeDropdown } from "components/dropdowns"
import { TranslationType } from "constants/menu"
import {
  generateTranslationParam,
  isAutoTranslationQueueExists,
  translationMandatoryUnfill,
} from "utils/translation_helper"
import { DEFAULT_DEBOUNCE_WAIT_TIME } from "constants/ui"
import {
  drawerBodyStyle,
  drawerContentStyle,
  baseDrawerFooterStyle,
  drawerFooterStyle,
} from "./styles"
import { TranslationDrawer } from "./TranslationDrawer/TranslationDrawer"

interface CategoryDrawerProps extends FormComponentProps {
  visible: boolean
  onClose(e: any): void
}

export const CategoryDrawerComponent: FC<CategoryDrawerProps> = ({
  visible,
  onClose,
  form,
}: CategoryDrawerProps) => {
  const dispatch = useDispatch()
  const {
    merchantID,
    drawer: { category, createdSellingTimeID },
    menu: { sections },
    sellingTimes,
    categories,
    isEditAllowed,
    isSellingTimeStructure,
    merchantGroupID,
    menuGroupDetail: { menuGroupID, languageSettings },
    outletV2,
    enableMenuTranslationPortal,
    autoTranslationQueue,
    originalTranslationFromDSMap,
  } = useSelector((state: AppState) => state?.menu)

  const [selectedSellingTime, setSelectedSellingTime] = useState<
    SellingTime | undefined
  >(sellingTimes[0])

  const { getFieldDecorator, getFieldValue, isFieldsTouched } = form

  const isEdit = !!category?.categoryID

  const menuTranslationSetting = menuGroupID
    ? languageSettings
    : outletV2?.menuTranslationSetting
  const isMenuTranslation =
    (enableMenuTranslationPortal &&
      isMenuSupportedLanguages(menuTranslationSetting)) ||
    false
  const isAutoTranslationEnabled =
    isMenuTranslation && !!menuTranslationSetting?.isAutoTranslationEnabled

  const [showTranslationDrawer, setShowTranslationDrawer] = useState(false)
  const [translationUpdated, setTranslationUpdated] = useState(false)

  const nameTranslation = category?.nameTranslation
  const [translationValue, setTranslationValue] = useState<
    TranslationSetting | undefined
  >(nameTranslation)

  const sectionLabel = useTranslation("menu.drawers.category.labels.section")

  const textMap = useTranslationsMapTyped({
    deleteCategory: "menu.drawers.category.buttons.deleteCategory",
    createSuccess: "menu.notifications.createCategory.successMessage",
    updateSuccess: "menu.notifications.updateCategory.successMessage",
    deleteSuccess: "menu.notifications.deleteCategory.successMessage",
    createSuccessDescription:
      "menu.notifications.createCategory.successDescription",
    errorRequired: "menu.errors.requiredField",
    errorDuplicateCategoryName: "menu.errors.category.duplicateCategoryName",
    errorExceedMaxCategoryLimit: "menu.errors.category.exceedMaxCategoryLimit",
    errorCharacterLimit: "menu.errors.characterLimit",
    errorArchived: "menu.errors.category.archived",
    errorDuplicate: "menu.errors.price.duplicateCategoryName",
    sellingTimeLabel: "menu.sellingTime.label.sellingTime",
    menuDrawerCategoryInDefaultLanguage:
      "menu.drawers.item.labels.categoryInDefaultLanguage",
    translateButtonText: "menu.drawers.item.buttons.translate",
    categoryLabel: "menu.drawers.category.labels.name",
    saveChanges: "menu.buttons.saveChanges",
    addCategory: "menu.drawers.category.buttons.addCategory",
    menuTranslationTitle: "menuTranslation.title",
    menuTranslationEditTranslation: "menuTranslation.editTranslation",
    menuTranslationItemLabel: "menuTranslation.itemLabel",
    menuTranslationErrorAutoTranslation: "menuTranslation.errorAutoTranslation",
    menuTranslationDetailCategory: "menuTranslation.detail.category",
    menuTranslationLoadingAutoTranslation:
      "menuTranslation.loadingAutoTranslation",
  })

  const isAutoTranslationLoading = isAutoTranslationQueueExists({
    queue: autoTranslationQueue,
    entityType: TranslationType.category,
  })

  const getSaveButtonText = () => {
    if (isAutoTranslationLoading) {
      return textMap.menuTranslationLoadingAutoTranslation
    }
    return isEdit ? textMap.saveChanges : textMap.addCategory
  }

  const conflictConfirmPayload = useConflictConfirmPayload(category)

  useEffect(() => {
    if (isEdit) {
      // is edit
      setSelectedSellingTime(
        getSellingTimeByID(category?.sellingTimeID, sellingTimes),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit])

  // set selected selling time as the new created selling time if created while this drawer still opened
  useEffect(() => {
    if (createdSellingTimeID) {
      setSelectedSellingTime(
        getSellingTimeByID(createdSellingTimeID, sellingTimes),
      )
      // clear the created selling time
      dispatch(
        actionCreator.menu.toggleDrawer({
          createdSellingTimeID: null,
        }),
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sellingTimes])

  const errorCallback = (response: ResponseWithError) => {
    const { message = "", target = "" } = response ?? {}

    switch (target) {
      case "ExceedMaxCategoryLimit":
      case "ExceedMaxCategoryLimitForNotArchived":
        return replace(textMap.errorExceedMaxCategoryLimit, "$count", message)
      case "CategoryIsArchived":
        return textMap.errorArchived
      case "DuplicateCategoryName":
        return textMap.errorDuplicate
      default:
        return true
    }
  }

  const getSellingTimeInfoEventLabels = () => {
    if (isSellingTimeStructure) {
      const previousSellingTime = isEdit
        ? getSellingTimeByID(category?.sellingTimeID, sellingTimes)
        : sellingTimes[0]
      const newSellingTime = selectedSellingTime
      if (!previousSellingTime || !newSellingTime) return {}
      return {
        PREVIOUS_SELLING_TIME_INFO: getSellingTimeMetadata(previousSellingTime),
        SELLING_TIME_INFO: getSellingTimeMetadata(newSellingTime),
      }
    }
    return {}
  }

  const handleSave = () => {
    form.validateFields(async (err, formValues) => {
      if (err) return

      const categoryData = {
        ...category,
        ...formValues,
        sellingTimeID: selectedSellingTime?.sellingTimeID ?? "",
      }

      const previousCategory = {
        name: category?.categoryName,
        categoryID: category?.categoryID ?? "",
        sectionID: category?.sectionID ?? "",
        sellingTimeID: category?.sellingTimeID ?? "",
        nameTranslation: category?.nameTranslation,
      }

      const updatedCategory = {
        name: categoryData.name,
        categoryID: categoryData?.categoryID ?? "",
        sectionID: categoryData?.sectionID ?? "",
        sellingTimeID: categoryData?.sellingTimeID ?? "",
        nameTranslation: generateTranslationParam({
          menuTranslationSetting,
          translationSetting: translationValue,
          originalTranslationFromDSMap,
          entity: "category",
          textType: "name",
          text: categoryData.name,
        }),
      }

      let resp
      let conflictStatus = EventConflictStatus.noConflict

      let labels: { [key: string]: any } = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: category?.categoryID,
        INTENT: isEdit ? "EDIT_CATEGORY" : "ADD_CATEGORY",
        PREVIOUS_CATEGORY_INFO: previousCategory,
        CATEGORY_INFO: updatedCategory,
        ...getSellingTimeInfoEventLabels(),
      }
      if (isEdit) {
        conflictStatus = conflictConfirmPayload.showConfirm
          ? EventConflictStatus.needConfirmation
          : EventConflictStatus.noConflict
        if (conflictConfirmPayload.showConfirm) {
          // show confirm modal
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewModifyCategory,
            action: EventAction.submitModifyCategory,
            labels: {
              ...labels,
              CONFLICT_STATUS: conflictStatus,
            },
          })
        }
        resp = await dispatch(
          actionCreator.menu.updateCategory({
            merchantID,
            categoryID: category?.categoryID ?? "",
            data: updatedCategory,
            isSellingTimeStructure,
            errorCallback,
            successMessage: textMap.updateSuccess,
            menuGroupID,
            merchantGroupID,
            ...conflictConfirmPayload,
          }),
        )
        if (resp) {
          // click confirm
          conflictStatus = conflictConfirmPayload.showConfirm
            ? EventConflictStatus.confirmed
            : EventConflictStatus.noConflict
        } else {
          // click cancel
          conflictStatus = EventConflictStatus.cancelled
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewModifyCategory,
            action: EventAction.submitModifyCategory,
            labels: {
              ...labels,
              CONFLICT_STATUS: conflictStatus,
            },
          })
          return
        }
      } else {
        resp = (await dispatch(
          actionCreator.menu.createCategory({
            merchantID,
            data: updatedCategory,
            errorCallback,
            successOptions: {
              message: textMap.createSuccess,
              description: textMap.createSuccessDescription,
            },
            menuGroupID,
            merchantGroupID,
          }) as AnyAction,
        )) as AxiosAction<CreateCategoryResponse>

        if (resp?.type.endsWith("_SUCCESS")) {
          const categoryID = resp?.payload?.data?.categoryID

          await dispatch(
            actionCreator.menu.fetchMenu({
              merchantID,
              isSellingTimeStructure,
              merchantGroupID,
              menuGroupID,
            }),
          )
          dispatch(actionCreator.menu.selectCategoryByID(categoryID ?? ""))
        }
      }

      labels = {
        ...labels,
        CONFLICT_STATUS: conflictStatus,
        STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }
      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewModifyCategory,
        action: EventAction.submitModifyCategory,
        labels,
      })
    })
  }

  const confirmDeleteCategoryOptions = ConfirmDeleteCategoryOptions(category)
  const handleDelete = async () => {
    const resp: any = await dispatch(
      actionCreator.menu.deleteCategory({
        merchantID,
        categoryID: category?.categoryID ?? "",
        confirmOptions: confirmDeleteCategoryOptions,
        successMessage: textMap.deleteSuccess,
        menuGroupID,
        merchantGroupID,
      }),
    )

    if (resp) {
      let labels = {
        ...(merchantID
          ? { STORE_ID: merchantID }
          : { MERCHANT_GROUP_ID: merchantGroupID, MENU_GROUP_ID: menuGroupID }),
        CATEGORY_ID: category?.categoryID,
        ENTRY_POINT: EventEntryPoint.menuOverviewModifyCategory,
        STATUS: "SUCCESS",
        REASON: "",
        ERROR_MSG: "",
      }

      if (resp?.type.endsWith("_FAIL")) {
        labels = {
          ...labels,
          STATUS: "FAILURE",
          ...getResponseErrorEventLabels(resp),
        }
      }

      TroyWebTracker.sendEvent({
        category: EventCategory.menuOverviewModifyCategory,
        action: EventAction.submitDeleteCategory,
        labels,
      })
    }
  }

  const handleValidateCategoryName = (
    _rule: any,
    value: any,
    callback: (message?: string) => void,
  ) => {
    const sectionID = getFieldValue("sectionID")
    if (isEmpty(sectionID) && isEmpty(value)) callback()
    const nameList = categories
      .filter(
        c => c.sectionID === sectionID && c.categoryID !== category?.categoryID,
      )
      .map(({ categoryName }) => categoryName)
    if (nameList.includes(value)) {
      callback(textMap.errorDuplicateCategoryName)
    } else {
      callback()
    }
  }

  // Only show error after a field is touched.
  const validationProps = getValidationProps(
    form,
    ["name", "sectionID"],
    isEditAllowed && isEdit,
  )

  const getSaveButtonDisabled = () => {
    if (isEdit && isEditAllowed) {
      if (
        (isFieldsTouched() ||
          category?.sellingTimeID !== selectedSellingTime?.sellingTimeID ||
          translationUpdated) &&
        !translationMissing()
      )
        return false
    }
    return (
      !isEditAllowed ||
      !isFieldsTouched() ||
      hasFormErrors(form) ||
      translationMissing()
    )
  }

  const openTranslationDrawer = () => {
    TroyWebTracker.sendEvent({
      category: EventCategoryEng.showTranslationDrawer,
      action: EventActionEng.showCategoryTranslation,
    })
    setShowTranslationDrawer(true)
  }

  const handleOnSaveTranslation = (translation?: TranslationDetail) => {
    if (translation) {
      setTranslationValue(translation.nameTranslation)
    }
    setShowTranslationDrawer(false)
    setTranslationUpdated(true)
    dispatch(
      actionCreator.menu.removeTranslationError(TranslationType.category),
    )
  }

  useEffect(() => {
    if (
      nameTranslation &&
      nameTranslation.originalText === getFieldValue("name")
    ) {
      setTranslationValue(nameTranslation)
    }
  }, [nameTranslation, getFieldValue])

  const menuTranslateGetTranslation = useCallback(
    debounce(() => {
      const textTargetLang =
        menuTranslationSetting?.translationModelSupportedLanguages
      const fieldValue = getFieldValue("name")
      if (textTargetLang?.length && fieldValue?.trim().length) {
        const data: MenuTranslationRequestData = {
          textType: "name",
          entity: "category",
          text: fieldValue,
          textSourceLang: menuTranslationSetting?.menuDefaultLanguage || "",
          textTargetLang,
        }
        dispatch(
          actionCreator.menu.getTranslation({
            data,
            merchantID,
            merchantGroupID,
            menuGroupID,
          }),
        )
        TroyWebTracker.sendEvent({
          category: EventCategoryEng.translationValueFetch,
          action: EventActionEng.categoryAutoTranslationFetch,
        })
      }
    }, DEFAULT_DEBOUNCE_WAIT_TIME),
    [dispatch],
  )

  const translationMissing = () => {
    if (!isMenuTranslation) {
      return false
    }
    if (
      category?.translationError &&
      category.translationError.has(getFieldValue("name"))
    ) {
      return true
    }
    if (isAutoTranslationEnabled) {
      return false
    }

    return translationMandatoryUnfill(
      menuTranslationSetting?.translationFieldMandatoryLanguages,
      translationValue,
    )
  }

  return (
    <Fragment>
      <Drawer
        destroyOnClose
        title={useTranslation(
          isEdit
            ? "menu.drawers.category.editCategory"
            : "menu.drawers.category.addNewCategory",
        )}
        width={450}
        open={visible}
        data-testid="category-drawer"
        onClose={e => {
          TroyWebTracker.sendEvent({
            category: EventCategory.menuOverviewModifyCategory,
            action: EventAction.clickExit,
            labels: {
              ...(merchantID
                ? { STORE_ID: merchantID }
                : {
                    MERCHANT_GROUP_ID: merchantGroupID,
                    MENU_GROUP_ID: menuGroupID,
                  }),
              CATEGORY_ID: category?.categoryID,
              INTENT: isEdit ? "EDIT_CATEGORY" : "ADD_CATEGORY",
            },
          })
          onClose(e)
        }}
      >
        <div css={drawerBodyStyle}>
          <div
            css={css`
              flex: 1 0 auto;
            `}
          >
            <div css={drawerContentStyle}>
              <Form.Item
                label={textMap.categoryLabel}
                {...validationProps.name}
              >
                {getFieldDecorator("name", {
                  initialValue: category?.categoryName,
                  rules: [
                    {
                      required: true,
                      message: textMap.errorRequired,
                    },
                    {
                      validator:
                        !getUCMConfig("disableNameUniquenessOnCategory") &&
                        handleValidateCategoryName,
                    },
                    {
                      max: getUCMConfig("maxLengthCategoryName"),
                      message: replace(
                        textMap.errorCharacterLimit,
                        "$limit",
                        getUCMConfig("maxLengthCategoryName"),
                      ),
                    },
                  ] as any,
                })(
                  <Input
                    placeholder={useTranslation(
                      "menu.drawers.category.placeholders.name",
                    )}
                    data-testid="form-name"
                    autoComplete="off"
                    disabled={!isEditAllowed}
                    onChange={() =>
                      isAutoTranslationEnabled && menuTranslateGetTranslation()
                    }
                  />,
                )}
              </Form.Item>
              {isMenuTranslation && (
                <div>
                  <span>
                    {textMap.menuTranslationDetailCategory.replace(
                      "{x}",
                      menuTranslationSetting?.menuSupportedLanguages.length.toString() ||
                        "",
                    )}
                    <br />
                  </span>
                  <Button
                    type="link"
                    data-testid="category-translation"
                    onClick={openTranslationDrawer}
                  >
                    {textMap.menuTranslationEditTranslation}
                  </Button>
                  {translationMissing() && (
                    <div
                      css={css`
                        color: #ee6352;
                      `}
                      data-testid="category-translation-error"
                    >
                      {textMap.menuTranslationErrorAutoTranslation}
                    </div>
                  )}
                </div>
              )}
              {isSellingTimeStructure ? (
                <Fragment>
                  <Form.Item required label={textMap.sellingTimeLabel}>
                    <SellingTimeDropdown
                      disabled={!isEditAllowed}
                      sellingTimes={sellingTimes}
                      selectedSellingTime={selectedSellingTime}
                      eventTrackerObject={{
                        eventCategoryTracker:
                          EventCategory.menuOverviewModifyCategory,
                        categoryID: category?.categoryID,
                      }}
                      onChange={setSelectedSellingTime}
                    />
                  </Form.Item>
                  <ServiceHourList sellingTime={selectedSellingTime} />
                </Fragment>
              ) : (
                <Form.Item label={sectionLabel} {...validationProps.sectionID}>
                  {getFieldDecorator("sectionID", {
                    initialValue: category?.sectionID,
                    rules: [
                      {
                        required: true,
                        message: textMap.errorRequired,
                      },
                    ],
                  })(
                    <Select
                      css={css`
                        width: 100%;
                      `}
                      placeholder={sectionLabel}
                      disabled={!isEditAllowed || isEdit}
                    >
                      {sections?.map(({ sectionID, sectionName }) => (
                        <Select.Option key={sectionID} value={sectionID}>
                          {sectionName}
                        </Select.Option>
                      ))}
                    </Select>,
                  )}
                </Form.Item>
              )}
            </div>
          </div>
          {isEdit && (
            <div css={baseDrawerFooterStyle}>
              <Button
                danger
                block
                disabled={!isEditAllowed}
                onClick={handleDelete}
              >
                {textMap.deleteCategory}
              </Button>
            </div>
          )}
          <div css={drawerFooterStyle}>
            <Button
              type="primary"
              block
              onClick={handleSave}
              disabled={getSaveButtonDisabled()}
              loading={isAutoTranslationLoading}
            >
              {getSaveButtonText()}
            </Button>
          </div>
        </div>
      </Drawer>
      {isMenuTranslation && (
        <TranslationDrawer
          data-testid="translation-drawer"
          translationType={TranslationType.category}
          visible={showTranslationDrawer}
          merchantID={merchantID}
          merchantGroupID={merchantGroupID}
          menuGroupID={menuGroupID}
          menuTranslationSetting={menuTranslationSetting}
          nameTranslation={translationValue}
          resetNameTranslation={category?.resetNameTranslation}
          defaultValue={getFieldValue("name")}
          onClose={() => setShowTranslationDrawer(false)}
          onSave={handleOnSaveTranslation}
          disabled={!isEditAllowed}
          isAutoTranslationEnabled={isAutoTranslationEnabled}
          isEdit={isEdit}
          autoTranslationQueue={autoTranslationQueue}
        />
      )}
    </Fragment>
  )
}
export const CategoryDrawer = Form.create<CategoryDrawerProps>({})(
  CategoryDrawerComponent,
)
